import React from 'react';
import PropTypes from 'prop-types';
import { graphql, gql, compose } from 'react-apollo';
import { Button, Modal } from 'react-bootstrap';
import cx from 'classnames';

// Style
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './BannerSettings.css';
import cp from '../../../components/commonStyle.css';

// Components
import Loader from '../../../components/Loader';
import MarketingBannerForm from '../../../components/siteadmin/MarketingBannerForm/MarketingBannerForm';
import { photosShow } from '../../../helpers/photosShow';
import { homebanneruploadDir } from '../../../config';

/**
 * Campaign Manager - Ad Server Professional v2
 * CRUD completo para gestionar múltiples campañas publicitarias
 */
class BannerSettings extends React.Component {

  static propTypes = {
    title: PropTypes.string.isRequired,
    data: PropTypes.shape({
      loading: PropTypes.bool,
      getAllMarketingBanners: PropTypes.array,
      refetch: PropTypes.func
    }),
  };

  static defaultProps = {
    data: {
      loading: true,
      getAllMarketingBanners: []
    }
  };

  state = {
    showModal: false,
    editingCampaign: null,  // null = Create Mode, object = Edit Mode
    statusFilter: 'ALL',
    deleteConfirm: null     // Campaign ID to confirm delete
  };

  // ═══════════════════════════════════════════════════════════════
  // MODAL HANDLERS
  // ═══════════════════════════════════════════════════════════════
  
  openCreateModal = () => {
    this.setState({ showModal: true, editingCampaign: null });
  };

  openEditModal = (campaign) => {
    this.setState({ showModal: true, editingCampaign: campaign });
  };

  closeModal = () => {
    this.setState({ showModal: false, editingCampaign: null });
    // Refetch data after modal closes
    if (this.props.data.refetch) {
      this.props.data.refetch();
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // DELETE HANDLERS
  // ═══════════════════════════════════════════════════════════════
  
  confirmDelete = (campaign) => {
    this.setState({ deleteConfirm: campaign });
  };

  cancelDelete = () => {
    this.setState({ deleteConfirm: null });
  };

  executeDelete = async () => {
    const { deleteConfirm } = this.state;
    if (!deleteConfirm) return;

    try {
      await this.props.deleteMarketingBanner({
        variables: { id: deleteConfirm.id }
      });
      this.setState({ deleteConfirm: null });
      if (this.props.data.refetch) {
        this.props.data.refetch();
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Error al eliminar la campaña');
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // STATUS BADGE COMPONENT
  // ═══════════════════════════════════════════════════════════════
  
  renderStatusBadge = (status) => {
    const badgeStyles = {
      ACTIVE: { bg: '#d4edda', color: '#155724', label: '● ACTIVO' },
      PAUSED: { bg: '#fff3cd', color: '#856404', label: '⏸ PAUSADO' },
      COMPLETED: { bg: '#e2e3e5', color: '#383d41', label: '✓ COMPLETADO' }
    };
    const style = badgeStyles[status] || badgeStyles.COMPLETED;
    
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        backgroundColor: style.bg,
        color: style.color
      }}>
        {style.label}
      </span>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // PRIORITY BAR COMPONENT
  // ═══════════════════════════════════════════════════════════════
  
  renderPriorityBar = (weight) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{
          width: '80px',
          height: '8px',
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${weight}%`,
            height: '100%',
            backgroundColor: weight > 70 ? '#28a745' : weight > 40 ? '#ffc107' : '#6c757d',
            borderRadius: '4px',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <span style={{ fontSize: '12px', color: '#6c757d', minWidth: '30px' }}>
          {weight}%
        </span>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // CAMPAIGN TYPE BADGE
  // ═══════════════════════════════════════════════════════════════
  
  renderTypeBadge = (type) => {
    const types = {
      CPC: { emoji: '💰', label: 'CPC' },
      CPM: { emoji: '👁️', label: 'CPM' },
      PERMANENT: { emoji: '🔄', label: 'PERM' }
    };
    const t = types[type] || types.PERMANENT;
    
    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '500',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6'
      }}>
        {t.emoji} {t.label}
      </span>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER CAMPAIGN ROW
  // ═══════════════════════════════════════════════════════════════
  
  renderCampaignRow = (campaign) => {
    const imagePath = photosShow(homebanneruploadDir) + campaign.image;
    
    return (
      <tr 
        key={campaign.id}
        style={{
          borderBottom: '1px solid #eee',
          transition: 'background-color 0.2s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        {/* Preview */}
        <td style={{ padding: '12px', width: '80px' }}>
          <div style={{
            width: '60px',
            height: '40px',
            borderRadius: '6px',
            backgroundImage: `url(${imagePath})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            border: '1px solid #dee2e6'
          }} />
        </td>
        
        {/* Nombre */}
        <td style={{ padding: '12px' }}>
          <div style={{ fontWeight: '600', color: '#333', marginBottom: '2px' }}>
            {campaign.title}
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>
            ID: {campaign.id}
          </div>
        </td>
        
        {/* Tipo */}
        <td style={{ padding: '12px', textAlign: 'center' }}>
          {this.renderTypeBadge(campaign.campaignType)}
        </td>
        
        {/* Prioridad */}
        <td style={{ padding: '12px' }}>
          {this.renderPriorityBar(campaign.priorityWeight)}
        </td>
        
        {/* Stats */}
        <td style={{ padding: '12px' }}>
          <div style={{ fontSize: '13px' }}>
            <span style={{ color: '#6c757d' }}>👁️</span> {campaign.currentViews?.toLocaleString() || 0}
            <span style={{ margin: '0 8px', color: '#ddd' }}>|</span>
            <span style={{ color: '#6c757d' }}>🖱️</span> {campaign.currentClicks?.toLocaleString() || 0}
          </div>
          {campaign.ctr > 0 && (
            <div style={{ fontSize: '11px', color: '#28a745', marginTop: '2px' }}>
              CTR: {campaign.ctr}%
            </div>
          )}
        </td>
        
        {/* Status */}
        <td style={{ padding: '12px', textAlign: 'center' }}>
          {this.renderStatusBadge(campaign.campaignStatus)}
        </td>
        
        {/* Actions */}
        <td style={{ padding: '12px', textAlign: 'right' }}>
          <Button
            bsSize="small"
            style={{ 
              marginRight: '8px',
              borderRadius: '6px',
              border: '1px solid #0056b3',
              backgroundColor: 'transparent',
              color: '#0056b3'
            }}
            onClick={() => this.openEditModal(campaign)}
          >
            ✏️ Editar
          </Button>
          <Button
            bsSize="small"
            bsStyle="danger"
            style={{ borderRadius: '6px' }}
            onClick={() => this.confirmDelete(campaign)}
          >
            🗑️
          </Button>
        </td>
      </tr>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // EMPTY STATE
  // ═══════════════════════════════════════════════════════════════
  
  renderEmptyState = () => (
    <div style={{
      textAlign: 'center',
      padding: '60px 20px',
      color: '#6c757d'
    }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📢</div>
      <h3 style={{ marginBottom: '8px', color: '#333' }}>No hay campañas</h3>
      <p style={{ marginBottom: '24px' }}>
        Crea tu primera campaña publicitaria para comenzar
      </p>
      <Button
        bsStyle="primary"
        onClick={this.openCreateModal}
        style={{
          borderRadius: '8px',
          padding: '10px 24px',
          fontWeight: '600'
        }}
      >
        + Crear Primera Campaña
      </Button>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // DELETE CONFIRMATION MODAL
  // ═══════════════════════════════════════════════════════════════
  
  renderDeleteModal = () => {
    const { deleteConfirm } = this.state;
    if (!deleteConfirm) return null;

    return (
      <Modal show={true} onHide={this.cancelDelete}>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Estás seguro de que deseas eliminar la campaña <strong>"{deleteConfirm.title}"</strong>?</p>
          <p style={{ color: '#dc3545', fontSize: '13px' }}>
            Esta acción no se puede deshacer. Se perderán todas las estadísticas.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.cancelDelete}>Cancelar</Button>
          <Button bsStyle="danger" onClick={this.executeDelete}>
            🗑️ Eliminar Campaña
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════

  render() {
    const { data: { loading, getAllMarketingBanners }, title } = this.props;
    const { showModal, editingCampaign, statusFilter } = this.state;

    if (loading) {
      return <Loader type={"text"} />;
    }

    // Filter campaigns by status
    const campaigns = getAllMarketingBanners || [];
    const filteredCampaigns = statusFilter === 'ALL' 
      ? campaigns 
      : campaigns.filter(c => c.campaignStatus === statusFilter);

    // Stats summary
    const activeCampaigns = campaigns.filter(c => c.campaignStatus === 'ACTIVE').length;
    const totalViews = campaigns.reduce((sum, c) => sum + (c.currentViews || 0), 0);
    const totalClicks = campaigns.reduce((sum, c) => sum + (c.currentClicks || 0), 0);

    return (
      <div className={cx(s.pagecontentWrapper, 'pagecontentWrapperRTL')}>
        <div className={s.container}>
          
          {/* ══════════════════════════════════════════════════════════
              HEADER
          ══════════════════════════════════════════════════════════ */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: '700' }}>
                📢 {title || 'Gestor de Campañas'}
              </h1>
              <p style={{ margin: 0, color: '#6c757d', fontSize: '14px' }}>
                Ad Server v2 - Rotación Ponderada
              </p>
            </div>
            <Button
              bsStyle="primary"
              onClick={this.openCreateModal}
              style={{
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '18px' }}>+</span> Nueva Campaña
            </Button>
          </div>

          {/* ══════════════════════════════════════════════════════════
              STATS SUMMARY
          ══════════════════════════════════════════════════════════ */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            {[
              { label: 'Total Campañas', value: campaigns.length, icon: '📊' },
              { label: 'Activas', value: activeCampaigns, icon: '✅', color: '#28a745' },
              { label: 'Vistas Totales', value: totalViews.toLocaleString(), icon: '👁️' },
              { label: 'Clicks Totales', value: totalClicks.toLocaleString(), icon: '🖱️' }
            ].map((stat, idx) => (
              <div key={idx} style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                border: '1px solid #eee'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>{stat.icon}</div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700',
                  color: stat.color || '#333'
                }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* ══════════════════════════════════════════════════════════
              FILTER TABS
          ══════════════════════════════════════════════════════════ */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '16px',
            borderBottom: '1px solid #eee',
            paddingBottom: '16px'
          }}>
            {['ALL', 'ACTIVE', 'PAUSED', 'COMPLETED'].map(filter => (
              <button
                key={filter}
                onClick={() => this.setState({ statusFilter: filter })}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: statusFilter === filter ? '#0056b3' : '#f8f9fa',
                  color: statusFilter === filter ? '#fff' : '#6c757d',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {filter === 'ALL' ? 'Todas' : filter === 'ACTIVE' ? 'Activas' : filter === 'PAUSED' ? 'Pausadas' : 'Completadas'}
                {' '}
                ({filter === 'ALL' ? campaigns.length : campaigns.filter(c => c.campaignStatus === filter).length})
              </button>
            ))}
          </div>

          {/* ══════════════════════════════════════════════════════════
              CAMPAIGNS TABLE
          ══════════════════════════════════════════════════════════ */}
          {filteredCampaigns.length === 0 ? (
            this.renderEmptyState()
          ) : (
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              overflow: 'hidden'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ 
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #eee'
                  }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>PREVIEW</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>NOMBRE</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>TIPO</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>PRIORIDAD</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>STATS</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>STATUS</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>ACCIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCampaigns.map(campaign => this.renderCampaignRow(campaign))}
                </tbody>
              </table>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════
              CREATE/EDIT MODAL
          ══════════════════════════════════════════════════════════ */}
          <Modal 
            show={showModal} 
            onHide={this.closeModal}
            bsSize="large"
            backdrop="static"
          >
            <Modal.Header closeButton style={{ borderBottom: '1px solid #eee' }}>
              <Modal.Title>
                {editingCampaign ? '✏️ Editar Campaña' : '➕ Nueva Campaña'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '0' }}>
              <MarketingBannerForm 
                initialValues={editingCampaign}
                onSuccess={this.closeModal}
                isModal={true}
              />
            </Modal.Body>
          </Modal>

          {/* Delete Confirmation Modal */}
          {this.renderDeleteModal()}

        </div>
      </div>
    );
  }
}

export default compose(
  withStyles(s, cp),
  graphql(gql`
    query getAllMarketingBanners($statusFilter: String) {
      getAllMarketingBanners(statusFilter: $statusFilter) {
        id
        title
        image
        link
        buttonText
        campaignType
        campaignStatus
        budgetLimit
        priorityWeight
        currentViews
        currentClicks
        ctr
        budgetProgress
        startDate
        endDate
        createdAt
      }
    }
  `, {
    options: {
      ssr: false,
      fetchPolicy: 'network-only'
    }
  }),
  graphql(gql`
    mutation deleteMarketingBanner($id: Int!) {
      deleteMarketingBanner(id: $id) {
        status
        id
        title
      }
    }
  `, {
    name: 'deleteMarketingBanner'
  })
)(BannerSettings); 